import Link from "next/link"
import { Button } from "@/components/ui/button"
import { ArrowRight, ShieldCheck } from "lucide-react"
import { RefObject } from "react"
import { BeforeAfterSlider } from "@/components/landing/before-after-slider"
import { formatFreePreviewTrustLine } from "@/lib/payments/free-preview-config"

interface HeroSection1Props {
  onTryItNow: () => void
  ctaText: string
  ctaRef?: RefObject<HTMLDivElement | null>
}

export function HeroSection1({ onTryItNow, ctaText, ctaRef }: HeroSection1Props) {
  return (
    <section className="relative overflow-hidden bg-background">
      {/* Ambient glow */}
      <div className="pointer-events-none absolute right-0 top-0 h-[600px] w-[600px] -translate-y-1/4 translate-x-1/4 rounded-full bg-primary/8 blur-3xl" />
      
      <div className="relative mx-auto max-w-[90rem] px-4 sm:px-6 lg:px-8">
        <div className="flex min-h-[calc(100vh-80px)] flex-col gap-8 py-8 lg:grid lg:grid-cols-[minmax(0,0.75fr)_minmax(0,1.25fr)] lg:items-center lg:gap-10 lg:py-0">
          
          {/* Hero Image - First on mobile, second on desktop */}
          <div className="relative order-first lg:order-last">
            <div className="overflow-hidden rounded-2xl border border-border/20 shadow-2xl shadow-black/50">
              <BeforeAfterSlider
                beforeImage="/hero/hero-preview1.webp"
                afterImage="/hero/hero-preview2.webp"
                beforeLabel="Before"
                afterLabel="New look"
                ariaLabel="Compare two wheel styles on the same blue sports car"
                alt="Blue sports car shown with the first wheel style"
                autoplay
                priority
                className="aspect-[1376/768]"
                imageClassName="object-cover"
                sizes="(max-width: 1024px) 100vw, 62vw"
              />
            </div>
          </div>

          {/* Copy - Second on mobile, first on desktop */}
          <div className="flex flex-col items-center text-center lg:items-start lg:text-left">
            {/* Headline */}
            <h1 className="text-balance text-4xl font-bold leading-[1.08] tracking-tight text-foreground sm:text-5xl lg:text-[3.5rem]">
              Try new wheels on your car — before you buy.
            </h1>
            
            {/* Subheadline */}
            <p className="mt-6 max-w-lg text-pretty text-lg leading-relaxed text-muted-foreground">
              Upload a photo of your car, choose a wheel, and get a realistic visual preview of the style on your actual car.
            </p>

            {/* CTAs */}
            <div className="mt-8 flex w-full flex-col gap-3 sm:mt-10 lg:w-full">
              <div ref={ctaRef} className="w-full">
                <Button 
                  size="lg" 
                  onClick={onTryItNow}
                  className="group h-14 w-full gap-2 rounded-lg bg-foreground px-8 text-base font-semibold text-background hover:bg-foreground/90 shadow-lg shadow-foreground/20 hover:shadow-foreground/30 transition-all sm:h-16"
                >
                  {ctaText === "Start for free" ? "Create my free preview" : ctaText}
                  <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Button>
              </div>
              <Link href="/credits" className="w-full">
                <Button 
                  size="lg" 
                  variant="outline"
                  className="h-12 w-full rounded-lg border-white/20 bg-transparent px-6 text-base font-medium text-foreground/80 hover:bg-white/5 hover:text-foreground hover:border-white/30 transition-all"
                >
                  View pricing
                </Button>
              </Link>
            </div>

            {/* Trust line (prominent but subtle) */}
            <div className="mt-4 flex w-full justify-center lg:justify-start">
              <div className="inline-flex flex-wrap items-center justify-center gap-2 rounded-full border border-border/50 bg-secondary/40 px-4 py-2 text-sm text-foreground/80 backdrop-blur-sm lg:justify-start">
                <ShieldCheck className="h-4 w-4 text-primary" />
                <span className="font-medium">{formatFreePreviewTrustLine().replace(/\.$/, "")}</span>
                <span className="text-foreground/60">- No credit card required</span>
              </div>
            </div>
          </div>

          </div>
      </div>
    </section>
  )
}
